import { LOCATION_TYPES, VIRTUAL_LOCATION_TYPE } from '../../constants/warehouseLocations';
import { NormalizeRack } from './payload';

/**
 * Modelo hierárquico das localizações (M1 r3) — funções PURAS compartilhadas
 * pela CASCATA (estilo pastas) e pela LISTA agrupada por rack/bloco.
 *
 * Cada porta-pallet é um RACK independente (prefixo de 1–2 letras) com BLOCOS
 * numerados em sequência — não existe rua nem lado. Três raízes, na ordem em
 * que o dono navega:
 *  - `racks`: Rack → [leaves diretas do rack: cabeceira/staging] + Bloco → Andar → Posição;
 *  - `fixed`: endereços físicos SEM rack (doca, staging de doca, fábrica,
 *    incidente), agrupados por tipo;
 *  - `virtual`: localizações virtuais (fora do galpão/mapa), agrupadas por setor.
 */

/** Virtual = fora do mapa. Aceita o boolean do DTO r3 e o type (defensivo). */
export function IsVirtualLocation(row) {
    return Boolean(row?.is_virtual) || row?.type === VIRTUAL_LOCATION_TYPE;
}

function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function byCode(a, b) {
    return String(a?.code ?? '').localeCompare(String(b?.code ?? ''));
}

/**
 * Constrói a árvore a partir das linhas do GET /locations (já filtradas pela
 * página). Agrupa por `row.rack` (letra, caixa alta) e `row.bay` (inteiro);
 * racks em ordem alfabética, blocos em ordem numérica. Blocos derivam
 * `bayWidth` da primeira posição com valor — no r3 a largura é do BLOCO
 * (wms_blocks) e o DTO da posição só a espelha.
 *
 * Devolve `{racks: [{rack, positionsCount, stagings, bays: [{bay, bayWidth,
 * positionsCount, levels: [{level, positions}]}]}], fixed, virtual, totalCount}`.
 */
export function BuildLocationsTree(rows) {
    const racksMap = new Map();
    const fixedMap = new Map();
    const virtualMap = new Map();
    let totalCount = 0;

    const rackEntry = (rack) => {
        if (!racksMap.has(rack)) {
            racksMap.set(rack, { rack, stagings: [], baysMap: new Map(), positionsCount: 0 });
        }
        return racksMap.get(rack);
    };

    for (const row of Array.isArray(rows) ? rows : []) {
        if (!row || typeof row !== 'object') {
            continue;
        }
        totalCount += 1;

        if (IsVirtualLocation(row)) {
            const sector = typeof row.sector === 'string' && row.sector.trim() ? row.sector.trim() : null;
            const key = sector ?? '';
            if (!virtualMap.has(key)) {
                virtualMap.set(key, { sector, items: [] });
            }
            virtualMap.get(key).items.push(row);
            continue;
        }

        const rack = NormalizeRack(row.rack);
        if (rack === null) {
            const type = row.type || 'OUTRO';
            if (!fixedMap.has(type)) {
                fixedMap.set(type, { type, items: [] });
            }
            fixedMap.get(type).items.push(row);
            continue;
        }

        const entry = rackEntry(rack);
        entry.positionsCount += 1;
        const bay = toFiniteNumber(row.bay);
        if (bay === null) {
            // Leaf direta do rack (cabeceira STG-A-00 e afins).
            entry.stagings.push(row);
            continue;
        }
        if (!entry.baysMap.has(bay)) {
            entry.baysMap.set(bay, { bay, bayWidth: null, positionsCount: 0, levelsMap: new Map() });
        }
        const bayEntry = entry.baysMap.get(bay);
        bayEntry.positionsCount += 1;
        const bayWidth = toFiniteNumber(row.bay_width);
        if (bayEntry.bayWidth === null && bayWidth !== null) {
            bayEntry.bayWidth = bayWidth;
        }
        const level = toFiniteNumber(row.level) ?? 0;
        if (!bayEntry.levelsMap.has(level)) {
            bayEntry.levelsMap.set(level, { level, positions: [] });
        }
        bayEntry.levelsMap.get(level).positions.push(row);
    }

    const racks = [...racksMap.values()]
        .sort((a, b) => a.rack.localeCompare(b.rack))
        .map((entry) => ({
            rack: entry.rack,
            positionsCount: entry.positionsCount,
            stagings: entry.stagings.sort(byCode),
            bays: [...entry.baysMap.values()]
                .sort((a, b) => a.bay - b.bay)
                .map((bayEntry) => ({
                    bay: bayEntry.bay,
                    bayWidth: bayEntry.bayWidth,
                    positionsCount: bayEntry.positionsCount,
                    levels: [...bayEntry.levelsMap.values()]
                        .sort((a, b) => a.level - b.level)
                        .map((levelEntry) => ({
                            level: levelEntry.level,
                            positions: levelEntry.positions.sort(
                                (a, b) => (toFiniteNumber(a.position) ?? 0) - (toFiniteNumber(b.position) ?? 0),
                            ),
                        })),
                })),
        }));

    // Fixas na ordem do enum (doca antes de fábrica etc.); tipos desconhecidos no fim.
    const typeOrder = new Map(LOCATION_TYPES.map((type, index) => [type, index]));
    const fixed = [...fixedMap.values()]
        .sort((a, b) => (typeOrder.get(a.type) ?? 99) - (typeOrder.get(b.type) ?? 99))
        .map((group) => ({ ...group, items: group.items.sort(byCode) }));

    // Virtuais por setor (alfabético; "sem setor" por último).
    const virtual = [...virtualMap.values()]
        .sort((a, b) => {
            if (a.sector === null) {
                return 1;
            }
            if (b.sector === null) {
                return -1;
            }
            return a.sector.localeCompare(b.sector);
        })
        .map((group) => ({ ...group, items: group.items.sort(byCode) }));

    return { racks, fixed, virtual, totalCount };
}
